/**
 * Persistent experiment store.
 *
 * Goals
 * - A user keeps the same variant across tabs and refreshes for COHORT_TTL_MS
 *   (default 30 days) so decision cycles are stable.
 * - Cross-tab consistency: localStorage is shared across tabs in the same
 *   origin, and we listen to `storage` events so a tab that assigns first wins.
 * - Backwards compatible: migrate any pre-existing sessionStorage values
 *   (`mfi_session_id`, `mfi_exp_<key>`) into localStorage on first read so
 *   in-flight users don't get re-bucketed.
 *
 * Storage shape (localStorage):
 *   mfi_cohort_id        -> string (stable visitor id, refreshed when expired)
 *   mfi_cohort_expires   -> number (epoch ms)
 *   mfi_exp_<key>        -> JSON { variant: string, expires: number }
 */

export const COHORT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const COHORT_ID_KEY = "mfi_cohort_id";
const COHORT_EXPIRES_KEY = "mfi_cohort_expires";
const EXP_PREFIX = "mfi_exp_";
const LEGACY_SESSION_KEY = "mfi_session_id";

type StoredAssignment = { variant: string; expires: number };

function safeLocal(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

/**
 * Returns a stable visitor id that survives refresh and is shared across tabs
 * for COHORT_TTL_MS. The id rotates when the window expires so cohorts
 * naturally roll over.
 */
export function getCohortId(): string {
  const local = safeLocal();
  const now = Date.now();

  if (local) {
    const id = local.getItem(COHORT_ID_KEY);
    const expRaw = local.getItem(COHORT_EXPIRES_KEY);
    const exp = expRaw ? Number(expRaw) : 0;
    if (id && exp > now) return id;

    // Migrate any in-flight sessionStorage id so the user keeps the same
    // identity for in-progress flows (drafts, exposures already logged).
    const session = safeSession();
    const legacy = session?.getItem(LEGACY_SESSION_KEY) ?? null;
    const fresh = legacy ?? newId();
    try {
      local.setItem(COHORT_ID_KEY, fresh);
      local.setItem(COHORT_EXPIRES_KEY, String(now + COHORT_TTL_MS));
    } catch {
      /* ignore quota errors */
    }
    // Mirror to sessionStorage for legacy readers still in this tab.
    try {
      session?.setItem(LEGACY_SESSION_KEY, fresh);
    } catch {
      /* noop */
    }
    return fresh;
  }

  // localStorage unavailable — fall back to sessionStorage so the value is at
  // least stable within the tab.
  const session = safeSession();
  if (session) {
    let id = session.getItem(LEGACY_SESSION_KEY);
    if (!id) {
      id = newId();
      try {
        session.setItem(LEGACY_SESSION_KEY, id);
      } catch {
        /* noop */
      }
    }
    return id;
  }
  return `s_${now}`;
}

function readAssignment(experimentKey: string): StoredAssignment | null {
  const local = safeLocal();
  const now = Date.now();
  const storageKey = `${EXP_PREFIX}${experimentKey}`;

  if (local) {
    const raw = local.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredAssignment;
        if (parsed && typeof parsed.variant === "string" && typeof parsed.expires === "number" && parsed.expires > now) {
          return parsed;
        }
        // Expired — drop it.
        local.removeItem(storageKey);
      } catch {
        // Legacy value: a plain string variant written by the old session-only
        // implementation. Honour it and upgrade to the new format with a TTL.
        const upgraded: StoredAssignment = { variant: raw ?? "control", expires: now + COHORT_TTL_MS };
        try {
          local.setItem(storageKey, JSON.stringify(upgraded));
        } catch {
          /* noop */
        }
        return upgraded;
      }
    }

    // Migrate any sessionStorage assignment so refresh doesn't reroll.
    const session = safeSession();
    const legacy = session?.getItem(storageKey);
    if (legacy) {
      const upgraded: StoredAssignment = { variant: legacy, expires: now + COHORT_TTL_MS };
      try {
        local.setItem(storageKey, JSON.stringify(upgraded));
      } catch {
        /* noop */
      }
      return upgraded;
    }
    return null;
  }

  // No localStorage — fall back to sessionStorage (tab-scoped only).
  const session = safeSession();
  const raw = session?.getItem(storageKey);
  if (!raw) return null;
  return { variant: raw, expires: now + COHORT_TTL_MS };
}

function writeAssignment(experimentKey: string, variant: string): StoredAssignment {
  const now = Date.now();
  const value: StoredAssignment = { variant, expires: now + COHORT_TTL_MS };
  const storageKey = `${EXP_PREFIX}${experimentKey}`;
  const local = safeLocal();
  if (local) {
    try {
      local.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* noop */
    }
  }
  // Mirror to sessionStorage so any legacy reader on the same tab agrees.
  try {
    safeSession()?.setItem(storageKey, variant);
  } catch {
    /* noop */
  }
  return value;
}

/**
 * Deterministically pick (and persist) a variant for the given experiment.
 * If a fresh assignment already exists in localStorage it is reused so the
 * user stays in the same cohort across tabs and refreshes.
 */
export function getOrAssignVariant(experimentKey: string, variants: string[]): string {
  if (variants.length === 0) return "control";
  const existing = readAssignment(experimentKey);
  if (existing && variants.includes(existing.variant)) return existing.variant;
  const pick = variants[Math.floor(Math.random() * variants.length)] ?? "control";
  writeAssignment(experimentKey, pick);
  return pick;
}

/**
 * Iterate every currently-active experiment assignment. Used when firing
 * conversion events so we credit every cohort the user is part of.
 */
export function listActiveAssignments(): Array<{ key: string; variant: string }> {
  const out: Array<{ key: string; variant: string }> = [];
  const now = Date.now();
  const local = safeLocal();
  const seen = new Set<string>();

  const collect = (storage: Storage | null) => {
    if (!storage) return;
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (!k || !k.startsWith(EXP_PREFIX)) continue;
      const key = k.slice(EXP_PREFIX.length);
      if (seen.has(key)) continue;
      const raw = storage.getItem(k);
      if (!raw) continue;
      let variant: string | null = null;
      try {
        const parsed = JSON.parse(raw) as StoredAssignment;
        if (parsed && typeof parsed.variant === "string" && typeof parsed.expires === "number") {
          if (parsed.expires <= now) continue;
          variant = parsed.variant;
        }
      } catch {
        variant = raw;
      }
      if (!variant) continue;
      seen.add(key);
      out.push({ key, variant });
    }
  };

  collect(local);
  collect(safeSession());
  return out;
}

/**
 * Subscribe to cross-tab changes to a specific experiment assignment. Returns
 * an unsubscribe function. Useful so a tab that lost a race re-renders with
 * the winning variant instead of diverging.
 */
export function subscribeToAssignment(
  experimentKey: string,
  callback: (variant: string | null) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const storageKey = `${EXP_PREFIX}${experimentKey}`;
  const handler = (e: StorageEvent) => {
    if (e.storageArea !== window.localStorage) return;
    if (e.key !== storageKey) return;
    if (!e.newValue) {
      callback(null);
      return;
    }
    try {
      const parsed = JSON.parse(e.newValue) as StoredAssignment;
      callback(parsed?.variant ?? null);
    } catch {
      callback(e.newValue);
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
